import React, { useMemo } from 'react'

import { Icon, Typography } from 'antd'

import { useSelectOptionsContext } from '../../context/selectOptions'
import DragIcon from '../../../../../../assets/images/dragIcon.png'
import { ManageOptionRelationshipsPopover } from '../ManageOptionRelationshipsPopover'
import { ViewOptionRelationshipsPopover } from '../ViewOptionRelationshipsPopover'

import './styles.less'

export function SelectOption({ optionId, selectedFieldId }) {
  const {
    editingOption,
    setEditingOption,
    showViewRelationshipsPopover,
    setShowViewRelationshipsPopover,
    showManageRelationshipsPopover,
    setShowManageRelationshipsPopover,
    relatedFieldIds,
    fieldRelationshipIds,
    updateOption,
    removeOption,
    options,
    optionRelationships
  } = useSelectOptionsContext()

  const option = useMemo(() => options?.entities[optionId], [options, optionId])

  const isEditingItem = useMemo(
    () => Object.keys(editingOption).length && option.id === editingOption.id,
    [editingOption, option]
  )

  function onCancelEditingOption() {
    setEditingOption({})
    setShowManageRelationshipsPopover(null)
  }

  async function onEditOption(updatedOption) {
    if (updatedOption.description !== option.description) {
      await updateOption({ option: updatedOption, fieldId: selectedFieldId })
    }
    onCancelEditingOption()
  }

  function onStartEditingOption(option) {
    setEditingOption(option)
    setShowManageRelationshipsPopover(option.id)
  }

  function handleInputKeydown({ key }) {
    if (key === 'Enter') onEditOption(editingOption)
    if (key === 'Escape') onCancelEditingOption()
  }

  const hasUnrelatedOptions = useMemo(
    () =>
      !!fieldRelationshipIds.length &&
      fieldRelationshipIds.some((id) => !optionRelationships?.entities[`${id}_${optionId}`]?.relatedOptionId),
    [fieldRelationshipIds.length, optionRelationships]
  )

  if (!option) return null

  return (
    <div className="selectOption">
      <img alt="drag-icon" src={DragIcon} />
      <div className={`selectOption__wrapper ${isEditingItem ? ' editing' : ''}`}>
        {isEditingItem ? (
          <input
            value={editingOption.description}
            onChange={({ target }) =>
              setEditingOption({ ...editingOption, value: target.value, description: target.value })
            }
            onKeyUp={handleInputKeydown}
            autoFocus
          />
        ) : (
          <Typography className="selectOption__optionLabel">
            {`${option?.description} `}
            <span className="selectOption__unrelated">{hasUnrelatedOptions && '*'}</span>
          </Typography>
        )}

        {isEditingItem ? (
          <div className="selectOption__btnWrapper">
            {relatedFieldIds?.length > 0 ? (
              <ManageOptionRelationshipsPopover
                selectedFieldId={selectedFieldId}
                option={option}
                visible={showManageRelationshipsPopover === option.id}
              >
                <Icon
                  type="check"
                  theme="outlined"
                  className="selectOption__checkIcon"
                  onClick={() => onEditOption(editingOption)}
                />
              </ManageOptionRelationshipsPopover>
            ) : (
              <Icon
                type="check"
                theme="outlined"
                className="selectOption__checkIcon"
                onClick={() => onEditOption(editingOption)}
              />
            )}
            <Icon type="close" theme="outlined" className="general-icon" onClick={() => onCancelEditingOption()} />
          </div>
        ) : (
          <div className="selectOption__btnWrapper">
            {relatedFieldIds?.length > 0 && (
              <ViewOptionRelationshipsPopover option={option} visible={showViewRelationshipsPopover === option.id}>
                <Icon
                  type="eye"
                  onClick={() => setShowViewRelationshipsPopover((prev) => (prev === option.id ? null : option.id))}
                />
              </ViewOptionRelationshipsPopover>
            )}
            <Icon type="edit" onClick={() => onStartEditingOption(option)} />
            <Icon type="delete" onClick={() => removeOption({ optionId: option.id, fieldId: selectedFieldId })} />
          </div>
        )}
      </div>
    </div>
  )
}
